<template>
  <ModalComp
    :isVisible="isVisible"
    :title="title"
    size="lg"
    @update:isVisible="$emit('update:isVisible', $event)"
  >
    <template #body>
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Libellé et Taux de remise -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <BaseInput
              v-model="localDiscount.libelle"
              label="Libellé"
              required
            />
          </div>
          <div>
            <BaseInput
              v-model="localDiscount.taux"
              label="Taux de remise (%)"
              type="number"
              min="1"
              max="100"
              step="1"
              required
            />
            <div v-if="validationErrors.taux" class="text-red-500 text-sm mt-1">
              {{ validationErrors.taux }}
            </div>
          </div>
        </div>

        <!-- Montant minimum et maximum -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <BaseInput
              v-model="localDiscount.montantMin"
              label="Montant minimum"
              type="number"
              min="0"
              step="0.01"
            />
            <div v-if="validationErrors.montantMin" class="text-red-500 text-sm mt-1">
              {{ validationErrors.montantMin }}
            </div>
          </div>
          <div>
            <BaseInput
              v-model="localDiscount.montantMax"
              label="Montant maximum"
              type="number"
              min="0"
              step="0.01"
            />
            <div v-if="validationErrors.montantMax" class="text-red-500 text-sm mt-1">
              {{ validationErrors.montantMax }}
            </div>
          </div>
        </div>

        <!-- Dates de début et fin -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <BaseInput
              v-model="localDiscount.dateDebut"
              label="Date de début"
              type="date"
              :max="maxStartDate"
              required
            />
          </div>
          <div>
            <BaseInput
              v-model="localDiscount.dateFin"
              label="Date de fin"
              type="date"
              :min="minEndDate"
              required
            />
          </div>
        </div>
        <div v-if="validationErrors.dateRange" class="text-red-500 text-sm mt-1">
          {{ validationErrors.dateRange }}
        </div>
      </form>
    </template>
    
    <template #footer>
      <div class="flex justify-end gap-3">
        <ActionButton
          label="Annuler"
          variant="secondary"
          @click="$emit('update:isVisible', false)"
        />
        <ActionButton
          label="Ajouter"
          variant="primary"
          :disabled="!isFormValid || submitting || localSubmitting"
          :loading="submitting || localSubmitting"
          @click="handleSubmit"
        />
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { reactive, computed, watch, ref } from 'vue'
import { ModalComp, ActionButton, BaseInput } from '@/components'

interface DiscountFormData {
  libelle: string
  montantMin: string
  montantMax: string
  dateDebut: string
  dateFin: string
  taux: string
  actif: string
}

interface ValidationErrors {
  montantMin: string
  montantMax: string
  taux: string
  dateRange: string
}

interface Props {
  isVisible: boolean
  title: string
  discountData: DiscountFormData
  validationErrors: ValidationErrors
  submitting?: boolean
}

interface Emits {
  (e: 'update:isVisible', value: boolean): void
  (e: 'submit', data: DiscountFormData): void
  (e: 'validate'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Local copy of discount data
const localDiscount = reactive<DiscountFormData>({
  libelle: '',
  montantMin: '',
  montantMax: '',
  dateDebut: '',
  dateFin: '',
  taux: '',
  actif: 'activé'
})

// Sync with props
watch(() => props.discountData, (newData) => {
  Object.assign(localDiscount, newData)
}, { immediate: true, deep: true })

// Validation des montants
const validateMontants = () => {
  const montantMin = parseFloat(localDiscount.montantMin)
  const montantMax = parseFloat(localDiscount.montantMax)
  const taux = parseFloat(localDiscount.taux)
  
  // Réinitialiser les erreurs
  const errors = {
    montantMin: '',
    montantMax: '',
    taux: ''
  }

  // Validation montant minimum (peut être 0 comme dans efashionparis)
  if (localDiscount.montantMin && montantMin < 0) {
    errors.montantMin = 'Le montant minimum ne peut pas être négatif'
  }

  // Validation montant maximum
  if (localDiscount.montantMax && montantMax < 0) {
    errors.montantMax = 'Le montant maximum ne peut pas être négatif'
  }

  // Validation montant max > montant min
  if (localDiscount.montantMin && localDiscount.montantMax && montantMax <= montantMin) {
    errors.montantMax = 'Le montant maximum doit être supérieur au montant minimum'
  }

  // Validation du taux (pourcentage entier requis par l'API GraphQL)
  if (localDiscount.taux) {
    if (taux <= 0 || taux > 100) {
      errors.taux = 'Le taux doit être entre 1 et 100%'
    } else if (!Number.isInteger(Number(localDiscount.taux))) {
      errors.taux = 'Le taux doit être un entier (pas de décimales)'
    }
  }

  emit('validate', errors)
}

// Validation des dates
const validateDates = () => {
  let dateRangeError = ''
  
  if (localDiscount.dateDebut && localDiscount.dateFin) {
    const dateDebut = new Date(localDiscount.dateDebut)
    const dateFin = new Date(localDiscount.dateFin)
    
    if (dateFin <= dateDebut) {
      dateRangeError = 'La date de fin doit être postérieure à la date de début'
    }
  }

  emit('validate', { dateRange: dateRangeError })
}

// Validation du formulaire
const isFormValid = computed(() => {
  // Vérifier que tous les champs requis sont remplis (montantMin et montantMax sont optionnels)
  const allFieldsFilled = localDiscount.libelle && 
                         localDiscount.dateDebut && 
                         localDiscount.dateFin && 
                         localDiscount.taux

  // Vérifier que la date de fin est après la date de début
  const datesValid = localDiscount.dateDebut && localDiscount.dateFin 
    ? new Date(localDiscount.dateFin) > new Date(localDiscount.dateDebut)
    : true

  const montantsValid = !props.validationErrors.montantMin && 
                       !props.validationErrors.montantMax && 
                       !props.validationErrors.taux &&
                       !props.validationErrors.dateRange

  return allFieldsFilled && datesValid && montantsValid
})

// Local immediate guard to prevent double-clicks before parent state propagates
const localSubmitting = ref(false)

// Reset localSubmitting when parent submitting stops or modal closes
watch(() => props.submitting, (val) => {
  if (val === false) localSubmitting.value = false
})
watch(() => props.isVisible, (visible) => {
  if (!visible) localSubmitting.value = false
})

// Helpers pour contraindre les dates comme sur le sélecteur de période du dashboard
const toISO = (d: Date) => d.toISOString().slice(0, 10)
const nextDay = (d: Date) => {
  const n = new Date(d)
  n.setDate(n.getDate() + 1)
  return n
}

// Fin doit être strictement après le début
const minEndDate = computed(() => {
  if (!localDiscount.dateDebut) return ''
  const s = new Date(localDiscount.dateDebut)
  return toISO(nextDay(s))
})

// Début doit être strictement avant la fin → max start = fin - 1 jour
const maxStartDate = computed(() => {
  if (!localDiscount.dateFin) return ''
  const e = new Date(localDiscount.dateFin)
  const prev = new Date(e)
  prev.setDate(prev.getDate() - 1)
  return toISO(prev)
})

// Correction en direct: si fin <= début, ajuster fin à début + 1 jour
watch([() => localDiscount.dateDebut, () => localDiscount.dateFin], () => {
  if (!localDiscount.dateDebut || !localDiscount.dateFin) return
  const s = new Date(localDiscount.dateDebut)
  const e = new Date(localDiscount.dateFin)
  if (e <= s) {
    localDiscount.dateFin = toISO(nextDay(s))
  }
})

// Watchers pour valider en temps réel
watch([() => localDiscount.montantMin, () => localDiscount.montantMax, () => localDiscount.taux], validateMontants)
watch([() => localDiscount.dateDebut, () => localDiscount.dateFin], validateDates)

const handleSubmit = () => {
  if (props.submitting || localSubmitting.value) return
  localSubmitting.value = true
  emit('submit', { ...localDiscount })
}
</script>
